import { useState } from "react";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import Logo from "../components/Logo";
import { InfinityLoop, SmileFigure, HouseHeart, UnitedFigures, WaveLine } from "../components/Illustrations";
import { Check, X, Copy, Download, ArrowUpRight } from "lucide-react";

const COLORS = [
  {
    name: "Mint",
    hex: "#C2FFE1",
    rgb: "194, 255, 225",
    role: "Primary brand accent",
    rule: "Use on dark surfaces. Reserve for headlines, the infinity loop, and key highlights — not body copy.",
    onLight: false,
  },
  {
    name: "Stone",
    hex: "#939597",
    rgb: "147, 149, 151",
    role: "Primary dark surface",
    rule: "Hero panels, photo overlays, and feature backgrounds. The 'serious' counterweight to mint.",
    onLight: false,
  },
  {
    name: "Ink",
    hex: "#2D3033",
    rgb: "45, 48, 51",
    role: "Body text & primary buttons",
    rule: "Default for all body type on cream. Primary CTA fill. Never use pure black.",
    onLight: false,
  },
  {
    name: "Cream",
    hex: "#F4F2ED",
    rgb: "244, 242, 237",
    role: "Body background",
    rule: "Page background and quiet sections. Slightly warm to humanise the brand.",
    onLight: true,
  },
  {
    name: "Mint Soft",
    hex: "#E5FBEF",
    rgb: "229, 251, 239",
    role: "Tertiary surface",
    rule: "Light section dividers, form fields, and subtle backgrounds. Use sparingly.",
    onLight: true,
  },
  {
    name: "Mint Deep",
    hex: "#9CE8C2",
    rgb: "156, 232, 194",
    role: "Hover & accessibility-tightened mint",
    rule: "Button hover states and any large mint type that needs WCAG AA contrast on stone.",
    onLight: false,
  },
];

const TYPE_SAMPLES = [
  { label: "Display H1", className: "font-headings text-6xl md:text-7xl tracking-tight", style: "Playfair Display · 400 · -0.02em", sample: "We help candidates and employers unite." },
  { label: "Heading H2", className: "font-headings text-4xl md:text-5xl tracking-tight", style: "Playfair Display · 400 · -0.02em", sample: "Technology recruitment that remembers it's about people." },
  { label: "Heading H3", className: "font-headings text-2xl md:text-3xl", style: "Playfair Display · 400", sample: "Four conversations between you and a job you'll love." },
  { label: "Lead paragraph", className: "font-body text-xl text-ink-700 font-light", style: "Outfit · 300 · 1.6 line-height", sample: "A boutique New Zealand technology recruitment partner that takes the time to understand the person on both sides of the table." },
  { label: "Body", className: "font-body text-base text-ink-700", style: "Outfit · 400 · 1.6 line-height", sample: "We don't blast your CV around town. We listen, then we make the introductions worth your time." },
  { label: "Overline / label", className: "font-body text-xs uppercase tracking-[0.25em] font-medium text-stone-dark", style: "Outfit · 500 · 0.25em tracking · uppercase", sample: "For candidates · Aotearoa NZ" },
];

const VOICE_PAIRS = [
  { do: "Honest, human, gently witty.", dont: "Corporate, jargon-heavy, hyperbolic." },
  { do: "“We listen first. Then we open doors.”", dont: "“Synergistic talent acquisition partner.”" },
  { do: "Specific verbs: shortlist, brief, place, listen.", dont: "Vague nouns: solutions, ecosystems, leverage." },
  { do: "Lowercase 'unite' as a verb in copy.", dont: "Capitalised 'Unite' inside running sentences." },
];

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard?.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1400);
        });
      }}
      data-testid={`copy-${value.replace("#", "")}`}
      className="inline-flex items-center gap-1.5 text-xs font-body opacity-70 hover:opacity-100 transition-opacity"
      aria-label={`Copy ${value}`}
    >
      <Copy size={12} strokeWidth={1.5} />
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

export default function Brand() {
  return (
    <div data-testid="page-brand">
      <Seo
        title="Brand guide"
        description="The Unite Recruitment brand guide — colour, type, logo, illustration, voice and motion. Everything a designer or partner needs to use our identity well."
        image="/og-brand.png"
        path="/brand"
      />
      <PageHero
        overline="Brand guide · v1.0"
        title="The Unite Recruitment brand, in one place."
        accentWord="brand"
        subtitle="Everything a designer, printer, or partner needs to use our identity well — colours, type, the wordmark, the illustration system, voice, and motion."
      />

      {/* TABLE OF CONTENTS */}
      <section className="pb-12 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex flex-wrap gap-3" data-testid="brand-toc">
            {[
              ["#overview", "Overview"],
              ["#logo", "Logo"],
              ["#colour", "Colour"],
              ["#type", "Typography"],
              ["#illustration", "Illustration"],
              ["#voice", "Voice & tone"],
              ["#motion", "Motion & sound"],
              ["#contact", "Contact"],
            ].map(([href, label]) => (
              <a
                key={href}
                href={href}
                className="text-xs uppercase tracking-[0.2em] font-body font-medium text-ink-700 hover:text-ink border border-ink-100 hover:border-ink rounded-full px-4 py-2 transition-colors"
              >
                {label}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section id="overview" className="py-16 md:py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-5">
            <p className="text-xs uppercase tracking-[0.25em] text-stone-dark font-medium mb-5">01 · Overview</p>
            <h2 className="font-headings text-4xl md:text-5xl text-ink tracking-tight leading-tight">
              A boutique brand for a boutique service.
            </h2>
          </div>
          <div className="md:col-span-7 space-y-5 font-body text-ink-700 text-lg leading-relaxed md:pt-4">
            <p>
              Unite Recruitment is human, considered, and quietly confident. Our
              identity is built around a single idea — <em>uniting</em> — and we
              express it through a tall, elegant serif wordmark, a warm earthy
              palette, and hand-drawn line illustrations that feel touched by a
              real person.
            </p>
            <p>
              Three rules sit above all others:{" "}
              <strong className="text-ink">restraint, warmth, and craft</strong>.
              If a design decision doesn't honour all three, it isn't us.
            </p>
          </div>
        </div>
      </section>

      {/* LOGO */}
      <section id="logo" className="py-20 md:py-28 bg-mint-soft">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.25em] text-stone-dark font-medium mb-5">02 · Logo</p>
            <h2 className="font-headings text-4xl md:text-5xl text-ink tracking-tight leading-tight">
              The wordmark.
            </h2>
            <p className="mt-5 font-body text-ink-700 text-lg leading-relaxed">
              Stacked, tall, serif. "Unite" leads; "Recruitment" sits beneath as
              a quiet, spaced uppercase label. Never set the wordmark in a
              single line.
            </p>
          </div>

          {/* Logo variants */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-cream rounded-2xl p-12 flex items-center justify-center min-h-[260px] border border-ink-100">
              <Logo size="lg" className="text-ink" />
            </div>
            <div className="bg-stone rounded-2xl p-12 flex items-center justify-center min-h-[260px]">
              <Logo size="lg" className="text-mint" />
            </div>
            <div className="bg-mint rounded-2xl p-12 flex items-center justify-center min-h-[260px]">
              <Logo size="lg" className="text-ink" />
            </div>
          </div>

          {/* Clear space */}
          <div className="bg-cream rounded-2xl p-8 md:p-12 border border-ink-100 mb-12">
            <p className="text-xs uppercase tracking-[0.25em] text-stone-dark font-medium mb-3">Clear space</p>
            <h3 className="font-headings text-2xl text-ink mb-6">Give it room to breathe.</h3>
            <p className="font-body text-ink-700 mb-8 max-w-2xl">
              Minimum clear space around the wordmark is equal to the cap-height
              of the "U" in <em>Unite</em>. Nothing competes inside that frame.
            </p>
            <div className="relative inline-block border border-dashed border-ink-200 p-10">
              <Logo size="lg" className="text-ink" />
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.2em] bg-cream px-2 text-stone-dark">
                clear space
              </span>
            </div>
          </div>

          {/* Do / Don't */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-cream rounded-2xl p-8 border border-ink-100">
              <div className="flex items-center gap-2 text-sm font-medium text-ink mb-5">
                <span className="w-7 h-7 rounded-full bg-mint flex items-center justify-center">
                  <Check size={14} strokeWidth={2} className="text-ink" />
                </span>
                Do
              </div>
              <ul className="space-y-3 font-body text-ink-700">
                <li>Use the stacked lockup at all sizes.</li>
                <li>Keep "Recruitment" in spaced uppercase (0.35em tracking).</li>
                <li>Place on ink, stone, mint, or cream surfaces only.</li>
                <li>Allow generous clear space — at least 1× cap-height.</li>
              </ul>
            </div>
            <div className="bg-cream rounded-2xl p-8 border border-ink-100">
              <div className="flex items-center gap-2 text-sm font-medium text-ink mb-5">
                <span className="w-7 h-7 rounded-full bg-ink flex items-center justify-center">
                  <X size={14} strokeWidth={2} className="text-mint" />
                </span>
                Don't
              </div>
              <ul className="space-y-3 font-body text-ink-700">
                <li>Don't set Unite + Recruitment on one line.</li>
                <li>Don't recolour the wordmark outside the approved palette.</li>
                <li>Don't outline, drop-shadow, or skew it.</li>
                <li>Don't place it on busy photography without a stone overlay.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* COLOUR */}
      <section id="colour" className="py-20 md:py-28 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.25em] text-stone-dark font-medium mb-5">03 · Colour</p>
            <h2 className="font-headings text-4xl md:text-5xl text-ink tracking-tight leading-tight">
              Two anchors, four supports.
            </h2>
            <p className="mt-5 font-body text-ink-700 text-lg leading-relaxed">
              <span className="text-ink font-medium">Mint</span> and{" "}
              <span className="text-ink font-medium">stone</span> carry the
              brand. <span className="text-ink font-medium">Ink</span> handles
              type and CTAs. Everything else is a quiet support.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {COLORS.map((c, i) => (
              <div
                key={c.hex}
                data-testid={`brand-colour-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-2xl overflow-hidden border border-ink-100 flex flex-col"
              >
                <div
                  className="h-32 md:h-40"
                  style={{ background: c.hex }}
                  aria-hidden
                />
                <div className="p-6 bg-cream flex-1 flex flex-col">
                  <div className="flex items-baseline justify-between mb-1">
                    <h3 className="font-headings text-2xl text-ink">{c.name}</h3>
                    <CopyButton value={c.hex} />
                  </div>
                  <p className="font-body text-xs text-stone-dark uppercase tracking-[0.18em] mb-3">
                    {c.role}
                  </p>
                  <dl className="grid grid-cols-2 gap-2 text-xs font-body text-ink-700 mb-4">
                    <dt className="text-stone-dark">HEX</dt>
                    <dd className="font-medium text-ink">{c.hex}</dd>
                    <dt className="text-stone-dark">RGB</dt>
                    <dd className="font-medium text-ink">{c.rgb}</dd>
                  </dl>
                  <p className="font-body text-sm text-ink-700 leading-relaxed mt-auto">
                    {c.rule}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Pair examples */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            <div className="bg-stone text-mint p-8 rounded-2xl">
              <p className="text-[10px] uppercase tracking-[0.25em] text-mint/70 mb-3">Pair · Hero</p>
              <p className="font-headings text-3xl leading-tight">Mint on stone</p>
              <p className="font-body text-sm text-cream/70 mt-2">Signature combination. Used in the hero wordmark.</p>
            </div>
            <div className="bg-mint text-ink p-8 rounded-2xl">
              <p className="text-[10px] uppercase tracking-[0.25em] text-stone-dark mb-3">Pair · Highlight</p>
              <p className="font-headings text-3xl leading-tight">Ink on mint</p>
              <p className="font-body text-sm text-ink-700 mt-2">For value cards, stats bands, and accent panels.</p>
            </div>
            <div className="bg-cream text-ink p-8 rounded-2xl border border-ink-100">
              <p className="text-[10px] uppercase tracking-[0.25em] text-stone-dark mb-3">Pair · Body</p>
              <p className="font-headings text-3xl leading-tight">Ink on cream</p>
              <p className="font-body text-sm text-ink-700 mt-2">Default reading combination across every page.</p>
            </div>
          </div>
        </div>
      </section>

      {/* TYPOGRAPHY */}
      <section id="type" className="py-20 md:py-28 bg-mint-soft">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-14">
            <div className="md:col-span-5">
              <p className="text-xs uppercase tracking-[0.25em] text-stone-dark font-medium mb-5">04 · Typography</p>
              <h2 className="font-headings text-4xl md:text-5xl text-ink tracking-tight leading-tight">
                Editorial serif. Modern sans. Nothing else.
              </h2>
            </div>
            <div className="md:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-cream p-8 rounded-2xl border border-ink-100">
                <p className="text-xs uppercase tracking-[0.25em] text-stone-dark font-medium mb-3">Display</p>
                <p className="font-headings text-7xl text-ink leading-none mb-3">Aa</p>
                <p className="font-headings text-2xl text-ink mb-2">Playfair Display</p>
                <p className="font-body text-sm text-ink-700">
                  Weights 400–700. Use 400 for display sizes (it's calmer); 600+ only for emphasis under 24px.
                </p>
              </div>
              <div className="bg-cream p-8 rounded-2xl border border-ink-100">
                <p className="text-xs uppercase tracking-[0.25em] text-stone-dark font-medium mb-3">Body & UI</p>
                <p className="font-body text-7xl text-ink leading-none mb-3 font-light">Aa</p>
                <p className="font-headings text-2xl text-ink mb-2">Outfit</p>
                <p className="font-body text-sm text-ink-700">
                  Weights 300–700. 400 for body, 300 for lead paragraphs, 500 for overlines and buttons.
                </p>
              </div>
            </div>
          </div>

          {/* Type scale samples */}
          <div className="bg-cream rounded-2xl border border-ink-100 divide-y divide-ink-100">
            {TYPE_SAMPLES.map((s, i) => (
              <div
                key={i}
                data-testid={`type-sample-${i}`}
                className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-12 gap-6 items-baseline"
              >
                <div className="md:col-span-3">
                  <p className="font-body font-medium text-sm text-ink">{s.label}</p>
                  <p className="font-body text-xs text-stone-dark mt-1">{s.style}</p>
                </div>
                <div className={`md:col-span-9 ${s.className} text-ink`}>{s.sample}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ILLUSTRATION */}
      <section id="illustration" className="py-20 md:py-28 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.25em] text-stone-dark font-medium mb-5">05 · Illustration</p>
            <h2 className="font-headings text-4xl md:text-5xl text-ink tracking-tight leading-tight">
              One continuous line.
            </h2>
            <p className="mt-5 font-body text-ink-700 text-lg leading-relaxed">
              All Unite illustrations are single-line drawings — drawn (or
              feeling drawn) in one stroke. They humanise the brand and reinforce
              the idea of connection without explaining it.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: "Infinity Loop", note: "Unity & continuity", Comp: InfinityLoop, w: "w-full", h: "h-32" },
              { name: "United Figures", note: "Candidate × Employer", Comp: UnitedFigures, w: "w-full", h: "h-44" },
              { name: "Smile Figure", note: "Welcoming candidate", Comp: SmileFigure, w: "w-24", h: "h-44" },
              { name: "House & Heart", note: "Community & home", Comp: HouseHeart, w: "w-full", h: "h-32" },
            ].map((it, i) => {
              const C = it.Comp;
              return (
                <div
                  key={i}
                  data-testid={`illustration-${i}`}
                  className="bg-stone rounded-2xl p-6 flex flex-col"
                >
                  <div className="flex-1 flex items-center justify-center min-h-[180px]">
                    <C className={`${it.w} ${it.h}`} stroke="#C2FFE1" strokeWidth={5} />
                  </div>
                  <p className="font-headings text-xl text-mint mt-4">{it.name}</p>
                  <p className="font-body text-xs uppercase tracking-[0.2em] text-cream/60 mt-1">{it.note}</p>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
            <div className="bg-mint-soft rounded-2xl p-8">
              <div className="flex items-center gap-2 text-sm font-medium text-ink mb-5">
                <span className="w-7 h-7 rounded-full bg-mint flex items-center justify-center"><Check size={14} strokeWidth={2} className="text-ink" /></span>
                Do
              </div>
              <ul className="space-y-3 font-body text-ink-700">
                <li>Stroke colour: mint #C2FFE1 on stone or ink; ink #2D3033 on mint.</li>
                <li>Stroke width: 4–6 px, rounded caps and joins.</li>
                <li>Treat illustrations as a single continuous line wherever possible.</li>
              </ul>
            </div>
            <div className="bg-mint-soft rounded-2xl p-8">
              <div className="flex items-center gap-2 text-sm font-medium text-ink mb-5">
                <span className="w-7 h-7 rounded-full bg-ink flex items-center justify-center"><X size={14} strokeWidth={2} className="text-mint" /></span>
                Don't
              </div>
              <ul className="space-y-3 font-body text-ink-700">
                <li>Don't fill the shapes — they are line art only.</li>
                <li>Don't add drop shadows, gradients, or 3-D effects.</li>
                <li>Don't combine with stock photography in the same frame.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* VOICE */}
      <section id="voice" className="py-20 md:py-28 bg-stone text-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.25em] text-mint font-medium mb-5">06 · Voice & tone</p>
            <h2 className="font-headings text-4xl md:text-5xl tracking-tight leading-tight">
              Honest, human, gently witty.
            </h2>
            <p className="mt-5 font-body text-cream/80 text-lg leading-relaxed">
              We talk to humans, not personas. We're warm but not chummy. We
              respect the reader's time, but we don't strip the personality out.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {VOICE_PAIRS.map((p, i) => (
              <div key={i} className="grid grid-cols-2 gap-4 bg-ink rounded-2xl p-6">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-mint mb-2">Do</p>
                  <p className="font-headings text-lg text-cream leading-snug">{p.do}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-cream/60 mb-2">Don't</p>
                  <p className="font-body text-sm text-cream/60 leading-snug line-through decoration-1">{p.dont}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MOTION */}
      <section id="motion" className="py-20 md:py-28 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
            <div className="md:col-span-5">
              <p className="text-xs uppercase tracking-[0.25em] text-stone-dark font-medium mb-5">07 · Motion & sound</p>
              <h2 className="font-headings text-4xl md:text-5xl text-ink tracking-tight leading-tight">
                One signature gesture, drawn once per page.
              </h2>
              <p className="mt-5 font-body text-ink-700 text-lg leading-relaxed">
                The infinity loop is the brand's one piece of motion. It draws
                itself in mint over a stone panel during every route change,
                accompanied (optionally) by a soft brushstroke whoosh. Nothing
                else animates with the same authority.
              </p>
              <ul className="mt-6 space-y-2 font-body text-ink-700 text-sm">
                <li>· Duration: ~1.1 s draw, ~2 s total transition.</li>
                <li>· Easing: cubic-bezier(0.65, 0, 0.35, 1).</li>
                <li>· Sound: opt-in via footer toggle, persisted in localStorage.</li>
                <li>· Reduced-motion users: panel without the draw animation.</li>
              </ul>
            </div>
            <div className="md:col-span-7 bg-stone rounded-2xl p-10 flex items-center justify-center">
              <InfinityLoop className="w-full h-44" stroke="#C2FFE1" strokeWidth={5} animate />
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT / DOWNLOAD */}
      <section id="contact" className="py-20 md:py-28 bg-mint">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16 text-center">
          <p className="text-xs uppercase tracking-[0.25em] text-stone-dark font-medium mb-5">08 · Use the brand</p>
          <h2 className="font-headings text-4xl md:text-5xl text-ink tracking-tight leading-tight">
            Building something for us, with us, or about us?
          </h2>
          <p className="mt-6 font-body text-ink-700 text-lg leading-relaxed max-w-2xl mx-auto">
            This page is the source of truth. Bookmark it, share the URL, and
            email <a href="mailto:Bronwyn@uniterecruitment.co.nz" className="underline">Bronwyn@uniterecruitment.co.nz</a> if you need source files (SVG logos, font licences, illustration files).
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="brand-print"
              className="inline-flex items-center justify-center gap-2 bg-ink text-mint hover:bg-stone-dark px-7 py-3.5 rounded-full font-body font-medium transition-transform hover:-translate-y-0.5"
            >
              <Download size={18} strokeWidth={1.5} />
              Print / Save as PDF
            </button>
            <Link
              to="/contact"
              data-testid="brand-cta-contact"
              className="inline-flex items-center justify-center gap-2 bg-transparent border border-ink/30 text-ink hover:bg-ink/5 px-7 py-3.5 rounded-full font-body font-medium transition-all"
            >
              Request source files
              <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
